"use client";

import * as React from "react";
import { useIsMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./sheet";

type DrawerSheetProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode | null;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  dismissible?: boolean;
  drawerContentClassName?: string;
  sheetContentClassName?: string;
  drawerHeaderClassName?: string;
  sheetHeaderClassName?: string;
  descriptionClassName?: string;
};

export function DrawerSheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  dismissible = true,
  drawerContentClassName,
  sheetContentClassName,
  drawerHeaderClassName,
  sheetHeaderClassName,
  descriptionClassName,
}: DrawerSheetProps) {
  // Intentional exception to our CSS-only responsive convention:
  // Drawer and Sheet are different overlay primitives, not just layout variants.
  // Rendering both and hiding one with CSS leaves duplicate dialogs/triggers in the
  // accessibility tree, so we choose the primitive after hydration.
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
        {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
        <DrawerContent
          className={cn(
            "max-h-[92dvh] overflow-hidden [&>div:first-child]:shrink-0",
            drawerContentClassName,
          )}
        >
          <DrawerHeader className={cn("shrink-0", drawerHeaderClassName)}>
            <DrawerTitle>{title}</DrawerTitle>
            {description && (
              <DrawerDescription className={descriptionClassName}>
                {description}
              </DrawerDescription>
            )}
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
            {children}
          </div>
          {footer && <div className="shrink-0">{footer}</div>}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen || dismissible) onOpenChange?.(nextOpen);
      }}
    >
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent
        className={cn("flex flex-col overflow-hidden", sheetContentClassName)}
        showClose={dismissible}
        onEscapeKeyDown={(event) => {
          if (!dismissible) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (!dismissible) event.preventDefault();
        }}
      >
        <SheetHeader className={cn("shrink-0", sheetHeaderClassName)}>
          <SheetTitle>{title}</SheetTitle>
          {description && (
            <SheetDescription className={descriptionClassName}>
              {description}
            </SheetDescription>
          )}
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
          {children}
        </div>
        {footer && <div className="shrink-0">{footer}</div>}
      </SheetContent>
    </Sheet>
  );
}
